import { FileTextOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Form, Space, Table, Typography } from 'antd';
import { useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import {
  formatAmount,
  getReportSumAmount,
  getReportSummarySum,
  getTableColumnsWithoutNull,
} from '../../../../common/utils/common.utils';
import CommonViewReport from '../../../../components/common/Invoice/ViewInvoiceInfo/CommonViewReport';
import ReportsHeader from '../../../../components/common/ReportsHeader';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import { loadingIndicator } from '../../../../components/common/spinner/LoadingIndicator';
import FormHeaderTitle from '../../../Invoice(Visa)/Components/FormHeaderTitle';
import { useGetDailySalesReportQuery } from '../endpoints/DailySalesReportEndpoints';
import { ISubmitDailySalesReport } from '../types/DailySalesReportTypes';
import { DailySalesReportColumn } from '../utils/DailySalesReportColumn';
import DownloadExcelReport from '../../../../components/common/ExcelReport';

const DailySalesReport = () => {
  const [paramData, setParamData] = useState<{
    date_range: string;
    id: number | string;
  }>();
  const [form] = Form.useForm();

  const [getData, SetGetData] = useState(true);
  const [pagination, setPagination] = useState({});
  const [formDate, setFromDate] = useState<string | Date | dayjs.Dayjs>(dayjs());
  const [toDate, setToDate] = useState<string | Date | dayjs.Dayjs>(dayjs());

  const [combClient, setCombClient] = useState('all');
  const [employeeId, setEmployeeId] = useState('all');
  const [categoryId, setCategoryId] = useState('all');

  const date_range_value = useWatch(['date_range'], form);
  // Set Default Value
  useEffect(() => {
    form.setFieldValue('date_range', [dayjs(), dayjs()]);
    form.setFieldValue('invoice_combclient_id', 'all');
    form.setFieldValue('employee_id', 'all');
    form.setFieldValue('category_id', 'all');
    setParamData({
      date_range: `?from_date=${dayjs()}&to_date=${dayjs()}`,
      id: '',
    });
  }, []);

  const location = useLocation();

  const { data, isLoading, isFetching } = useGetDailySalesReportQuery(
    {
      ...pagination,
      from_date: dayjs(formDate).format('YYYY-MM-DD'),
      to_date: dayjs(toDate).format('YYYY-MM-DD'),
      comb_client: combClient,
      employee_id: employeeId,
      category_id: categoryId,
    },
    { skip: getData }
  );
  let total_sales = getReportSumAmount(data?.data, 'invoice_net_total');
  let total_cost = getReportSumAmount(data?.data, 'cost_price');
  let total_service_charge = getReportSumAmount(data?.data, 'invoice_service_charge');
  let total_discount = getReportSumAmount(data?.data, 'invoice_discount');
  let total_profit = total_sales - total_cost;
  let total_collection = getReportSumAmount(data?.data, 'client_pay_amount');
  let total_due = getReportSumAmount(data?.data, 'due_amount');
  // This useEffect is working based on Params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const dailyValue = searchParams.get('daily');

    const monthFormDate = searchParams.get('from_date');
    const monthToDate = searchParams.get('to_date');

    const yearFormDate = searchParams.get('y_from_date');
    const yearToDate = searchParams.get('y_to_date');

    if (dailyValue) {
      handleFinish({
        date_range: [dailyValue, dailyValue],
        employee_id: 'all',
        invoice_combclient_id: 'all',
        category_id: 'all',
      });
    }

    if (monthFormDate && monthToDate) {
      form.setFieldValue('date_range', [dayjs(monthFormDate), dayjs(monthToDate)]);
      handleFinish({
        date_range: [monthFormDate, monthToDate],
        employee_id: 'all',
        invoice_combclient_id: 'all',
        category_id: 'all',
      });
    }

    if (yearFormDate && yearToDate) {
      form.setFieldValue('date_range', [dayjs(yearFormDate), dayjs(yearToDate)]);
      handleFinish({
        date_range: [yearFormDate, yearToDate],
        employee_id: 'all',
        invoice_combclient_id: 'all',
        category_id: 'all',
      });
    }
  }, [location.search]);

  const handleFinish = (value: ISubmitDailySalesReport) => {
    setCombClient(value.invoice_combclient_id);
    setEmployeeId(value.employee_id);
    setCategoryId(value.category_id);

    setFromDate(value.date_range[0]);
    setToDate(value.date_range[1]);

    SetGetData(false);

    setPagination((prev) => ({ ...prev }));
  };

  useEffect(() => {
    if (formDate && toDate) {
      setParamData({
        date_range: `from_date=${formDate}&to_date=${toDate}`,
        id: '',
      });
    }
  }, [formDate, toDate]);

  // DAILY SALES REPORT COLUMNS

  const salesData = data?.data?.map((item, index) => ({
    ...item,
    key: index + 1,
  }));

  const salesColumns = getTableColumnsWithoutNull(salesData, DailySalesReportColumn({ paramData }));

  const salesReportTableSummary = (_: any) => {
    return (
      <Table.Summary fixed>
        <Table.Summary.Row style={{ padding: 20 }}>
          <Table.Summary.Cell index={0} colSpan={6}>
            <div className='report_total_footer'>
              <Typography.Text strong>TOTAL </Typography.Text>
            </div>
          </Table.Summary.Cell>

          <Table.Summary.Cell index={0}>
            <div className='report_total_footer'>
              <Typography.Text strong>{total_sales}</Typography.Text>
            </div>
          </Table.Summary.Cell>

          <Table.Summary.Cell index={7}>
            <div className='report_total_footer'>
              <Typography.Text strong>{total_cost}</Typography.Text>
            </div>
          </Table.Summary.Cell>

          {total_discount ? (
            <Table.Summary.Cell index={7}>
              <div className='report_total_footer'>
                <Typography.Text strong>{formatAmount(total_discount)}</Typography.Text>
              </div>
            </Table.Summary.Cell>
          ) : (
            <></>
          )}

          {total_service_charge ? (
            <Table.Summary.Cell index={7}>
              <div className='report_total_footer'>
                <Typography.Text strong>{formatAmount(total_service_charge)}</Typography.Text>
              </div>
            </Table.Summary.Cell>
          ) : (
            <></>
          )}

          <Table.Summary.Cell index={7}>
            <div className='report_total_footer'>
              <Typography.Text strong>{formatAmount(total_profit)}</Typography.Text>
            </div>
          </Table.Summary.Cell>

          {total_collection ? (
            <Table.Summary.Cell index={75}>
              <div className='report_total_footer'>
                <Typography.Text strong>{formatAmount(total_collection)}</Typography.Text>
              </div>
            </Table.Summary.Cell>
          ) : (
            <></>
          )}

          {total_due ? (
            <Table.Summary.Cell index={37}>
              <div className='report_total_footer'>
                <Typography.Text strong>{formatAmount(total_due)}</Typography.Text>
              </div>
            </Table.Summary.Cell>
          ) : (
            <></>
          )}
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  const jsx_com = (
    <>
      <FormHeaderTitle title='Sales Report' />
      {
        <Table
          size='small'
          bordered
          columns={salesColumns}
          dataSource={salesData}
          scroll={{ x: 1500 }}
          loading={{
            spinning: isLoading || isFetching,
            indicator: loadingIndicator,
          }}
          pagination={false}
          summary={salesData?.length ? salesReportTableSummary : undefined}
        />
      }
    </>
  );

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Sales_Report${date_range_value}`,
  });

  // @HANDLE PRINT WITH HEADER FOOTER
  const print_content = (
    <div hidden>
      <CommonViewReport
        children={jsx_com}
        printRef={componentRef}
        title={{ info_title: '', title: 'Sales Report' }}
        extraInfo={{
          date:
            `${dayjs(formDate).format('DD-MM-YYYY')} to  
              ${dayjs(toDate).format('DD-MM-YYYY')}` || '',
        }}
      />
    </div>
  );

  return (
    <>
      <BreadCrumb arrOfOption={['Reports', 'Sales Report']} />
      <Space style={{ marginBottom: '1rem' }}>
        <Button type='primary' onClick={handlePrint}>
          <PrinterOutlined />
          Print
        </Button>
        <DownloadExcelReport
          excelName={`Sales_Report_from_date_${dayjs(formDate).format(
            'DD-MM-YYYY'
          )}_to_date_${dayjs(toDate).format('DD-MM-YYYY')}`}
          description='SALES REPORT'
          excelTableHead={[
            'Date',
            'Invoice No',
            'Client Name',
            'Category',
            'Sales By',
            'Sales Price',
            'Cost Price',
            'Discount',
            'Service Charge',
            'Profit',
            'Collect Amount',
            'Due Amount',
          ]}
          excelData={
            salesData?.map?.(
              ({
                due_amount,

                client_pay_amount,
                invoice_service_charge,
                invoice_discount,
                cost_price,
                invoice_net_total,
                employee_name,
                client_name,
                invoice_no,
                sales_date,
                invcat_title,
              }) => ({
                Date: dayjs(sales_date).format('DD-MM-YYYY'),
                'Invoice No': invoice_no,
                'Client Name': client_name,
                Category: invcat_title,
                'Sales By': employee_name,
                'Sales Price': invoice_net_total || 0,
                'Cost Price': cost_price || 0,
                Discount: invoice_discount || 0,
                'Service Charge': invoice_service_charge || 0,
                Profit: Number(invoice_net_total) - cost_price || 0,
                'Collect Amount': client_pay_amount || 0,
                'Due Amount': due_amount || 0,
              })
            ) ?? []
          }
        />
      </Space>
      <Form onFinish={handleFinish} form={form} layout='vertical'>
        <ReportsHeader
          size={5}
          showSelectClients
          showSelectEmployee
          // showSelectProduct
          date_range
          title='Sales Report'
          required
          showSelectInvoiceCategory
        />
      </Form>
      {jsx_com}

      {print_content}
    </>
  );
};

export default DailySalesReport;
